import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import FilterBar from '../../../components/ui/FilterBar'
import { businessToday, parseISODate, toISODate } from '../../../lib/datetime'
import {
  DASHBOARD_PERIODS,
  customRangeError,
  type DashboardManager,
  type DashboardPeriod,
  type DashboardQuery,
  type ScopeKind,
} from '../types'

interface Props {
  query: DashboardQuery
  onChange: (next: DashboardQuery) => void
  onClear: () => void
  /** Temizlenecek bir şey var mı? (varsayılanda "Filtreleri Temizle" gösterilmez — analiz §1.4.2) */
  showClear: boolean
  /** Kişi filtresi çizilsin mi? Yalnız **Raporum**'da kapatılır (orada kişi filtresi yoktur — §2.13). */
  showPeople: boolean
  people: DashboardManager[]
  /**
   * "Tümü" seçeneğinin etiketini belirler ("Tüm Yöneticiler" / "Tüm Bağlı Kullanıcılar" — analiz
   * §1.4.1.1). Kural sunucudan gelir (068 §K3); istemci yetki mantığını yeniden hesaplamaz.
   *
   * <p><b>Not (2026-08-23'te düzeltildi):</b> 2026-08-21'de buraya "`DIRECT_REPORTS`/`SELF` etiketi
   * uygulama akışından erişilemez" yazılmıştı — pano seçimi kapsam türüne bağlıydı, dolayısıyla kişi
   * filtresini yalnız `GENERAL_MANAGER` kapsamı görüyordu. Pano seçimi yetkiye taşındığından
   * (068 §K14) bu artık **doğru değil**: kapsamı daralmış yetkili kullanıcı Yönetici Raporu'nu görür ve
   * o hâlde `kind` `DIRECT_REPORTS`/`SELF` olur → ikinci etiket ("Tüm Bağlı Kullanıcılar") gerçekten
   * çizilir. Etiket o hâlde de **doğru** olanı yazar: küme fiilen kullanıcının kendi astlarıdır.
   */
  scopeKind: ScopeKind
  /**
   * Gösterilecek dönem seçenekleri — panoya göre değişir (Raporum dört, Yönetici Raporu altı seçenek;
   * 2026-08-19 revizyonu). Verilmezse tam liste kullanılır.
   */
  periods?: readonly DashboardPeriod[]
}

/**
 * "Tüm Yöneticiler" / "Tüm Bağlı Kullanıcılar" seçeneğinin değeri (analiz §1.4.1.1: "listenin ilk
 * seçeneği ... varsayılan seçim"). Sunucuya **gönderilmez**: seçiliyken sicil listesi boş gider ve
 * sunucu kapsamın tamamını uygular. Gerçek bir sicille çakışmaması için sicil biçiminde değildir.
 */
const ALL_OPTION = '__ALL__'

/**
 * Bugün, "YYYY-MM-DD" — "bitiş tarihi gelecek olamaz" kuralının ve takvim `maxDate`'inin referansı.
 *
 * <p><b>Kurumsal gün</b> (Europe/Istanbul) kullanılır, tarayıcının yerel günü değil: sunucu aynı kuralı
 * kurumsal günle uygular ({@code ActionDashboardService} → {@code BusinessTime.today()}). Ayrışırsa,
 * saat dilimi farklı bir istemcide kullanıcı sunucunun kabul edeceği bir günü **seçemez** (takvimde
 * kapalı görünür, seçilse `errorEndInFuture` alır).
 */
function todayIso(): string {
  return toISODate(businessToday())
}

/**
 * `Date` → "YYYY-MM-DD" veya `null`. {@code lib/datetime}'ın {@link toISODate}'ini sarar; tek fark boş
 * değerde `''` yerine **`null`** dönmesidir ({@code DashboardQuery.startDate/endDate} nullable'dır).
 * Eskiden burada gün bileşenlerini elle kuran bir kopya vardı — aynı mantığın iki yerde yaşaması,
 * birinin (ör. saat dilimi düzeltmesinde) güncellenip diğerinin unutulması riskini taşıyordu.
 */
function toIso(date: Date | null): string | null {
  // `|| null`: `toISODate` geçersiz bir Date'te `''` döner; boş string sorguya yazılırsa `isQueryReady`
  // ve `customRangeError` onu "dolu" sayar ve boş tarihli bir istek gidebilirdi. Bugün ulaşılamaz
  // (PrimeReact geçersiz girişte `null` yollar), yalnız savunma.
  return date ? toISODate(date) || null : null
}

/**
 * Panoların filtre çubuğu (068 — analiz §1.4.1): Kişi (çoklu) + Dönem + Özel Tarih Aralığı ve
 * "Filtreleri Temizle". Paylaşılan {@link FilterBar} kabuğu yeniden kullanılır (Reuse Mandate) → kontrol
 * yüksekliği/kenarlık ve yerleşim anayasadaki filtre çubuğu kurallarıyla uyumlu kalır.
 *
 * <p><b>"Yenile" butonu YOKTUR</b> (2026-08-19): iki pano da 30 saniyede bir kendiliğinden tazelenir →
 * elle yenileme gereksiz bir kontrol olurdu. 077 §K9 butonu Yönetici Raporu'ndan kaldırıp Raporum'da
 * bırakmıştı; o fark kaldırıldığı için {@code onRefresh}/{@code refreshing} prop'ları da düştü.
 * ({@link FilterBar} bu yeteneği korur — Aksiyonlar listesi gibi periyodik tazelenmeyen ekranlar
 * kullanmaya devam eder.)
 *
 * <p><b>Kişi filtresi çoklu seçimdir</b> (analiz §1.4.1.1) ve listenin <b>ilk seçeneği</b> kapsama göre
 * "Tüm Yöneticiler" / "Tüm Bağlı Kullanıcılar"dır; varsayılan seçim odur. Çoklu seçimde "tümü" ile tekil
 * kişilerin aynı anda işaretli kalması çelişkili olurdu, bu yüzden <b>birbirini dışlarlar</b>: "tümü"
 * işaretlenince tekil seçimler temizlenir, bir kişi seçilince "tümü" düşer. <b>Kapsamı sunucu söyler</b>
 * ({@code scopeKind}); istemci yetki mantığını yeniden hesaplamaz.
 *
 * <p><b>Kişi kümesi boşsa alan gizlenmez, {@code disabled} çizilir</b> (analiz §1.3.3; §1.12.1 ile
 * çelişiyordu, analist 2026-08-05'te "pasif görünsün" dedi).
 *
 * <p><b>Özel Tarih Aralığı</b> yalnız dönem {@code CUSTOM} iken açılır. Geçersiz aralıkta alan altında
 * uyarı görünür; istek {@code isQueryReady}/hook tarafında zaten tutulur (sunucu da ayrıca doğrular).
 */
export default function DashboardFilters({
  query,
  onChange,
  onClear,
  showClear,
  showPeople,
  people,
  scopeKind,
  periods = DASHBOARD_PERIODS,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const today = todayIso()
  // Takvim üst sınırı da AYNI kurumsal günden (yerel `new Date()` değil) — kural ile takvim ayrışmasın.
  const todayDate = businessToday()

  const allLabel =
    scopeKind === 'GENERAL_MANAGER' ? t('home.filters.allManagers') : t('home.filters.allReports')

  const peopleOptions = useMemo(
    () => [
      { label: allLabel, value: ALL_OPTION },
      ...people.map((m) => ({ label: m.fullName, value: m.employeeId })),
    ],
    [people, allLabel],
  )

  /** Seçim boşken "tümü" işaretli görünür (analiz §1.4.1.1 "varsayılan seçim Tüm Yöneticiler"). */
  const isAll = query.employeeIds.length === 0
  const selection = isAll ? [ALL_OPTION] : query.employeeIds

  /**
   * "Tümü" ile tekil kişiler birbirini dışlar: kullanıcı "tümü"yü <b>yeni</b> işaretlediyse tekil seçimler
   * temizlenir; bir kişi seçtiyse "tümü" listeden düşer. "Tümü"nün işaretini kaldırmak seçimi boşaltır ve
   * yine "tümü"ne döner (çoklu seçimde "hiçbiri" diye bir kapsam yoktur).
   */
  const changePeople = (next: string[]) => {
    const allWasSelected = selection.includes(ALL_OPTION)
    const allIsSelected = next.includes(ALL_OPTION)
    const ids = allWasSelected && allIsSelected ? next.filter((v) => v !== ALL_OPTION) : next
    onChange({ ...query, employeeIds: allIsSelected && !allWasSelected ? [] : ids })
  }

  const periodOptions = useMemo(
    () => periods.map((p) => ({ label: t(`home.period.${p}`), value: p })),
    [periods, t],
  )

  const rangeError = customRangeError(query, today)

  return (
    <FilterBar
      showClear={showClear}
      clearLabel={t('home.filters.clear')}
      onClear={onClear}
    >
      {showPeople && (
        <MultiSelect
          value={selection}
          options={peopleOptions}
          onChange={(e) => changePeople((e.value as string[]) ?? [])}
          // Kişi kümesi boşsa alan GİZLENMEZ, pasif çizilir (analist kararı 2026-08-05).
          disabled={people.length === 0}
          // Varsayılan "tümü" durumu chip DEĞİL düz metin çizilir: chip'in zeminli/× ikonlu görünümü
          // burada bir seçimi kaldırılabilir gösterirdi, oysa "tümü" kaldırılınca yine "tümü"ne döner.
          // Gerçek kişi seçimlerinde chip'e geçilir.
          display={isAll ? 'comma' : 'chip'}
          filter
          selectAllLabel={t('actions.filter.all')}
          maxSelectedLabels={2}
          selectedItemsLabel={t('home.filters.peopleSelected', { count: query.employeeIds.length })}
          aria-label={t('home.filters.person')}
          placeholder={allLabel}
          className="home-filter home-filter-people"
        />
      )}
      <Dropdown
        value={query.period}
        options={periodOptions}
        onChange={(e) => {
          const period = e.value as DashboardPeriod
          // Sabit döneme dönüldüğünde tarihler temizlenir; aksi hâlde CUSTOM'a geri dönünce eski
          // (ve artık görünmeyen) aralık sessizce yeniden uygulanırdı.
          onChange(
            period === 'CUSTOM'
              ? { ...query, period }
              : { ...query, period, startDate: null, endDate: null },
          )
        }}
        aria-label={t('home.filters.period')}
        className="home-filter"
      />
      {query.period === 'CUSTOM' && (
        <div className="home-filter-range">
          <Calendar
            value={parseISODate(query.startDate)}
            onChange={(e) => onChange({ ...query, startDate: toIso((e.value as Date) ?? null) })}
            dateFormat="dd.mm.yy"
            showIcon
            maxDate={todayDate}
            placeholder={t('home.filters.startDate')}
            aria-label={t('home.filters.startDate')}
            className={rangeError ? 'p-invalid' : undefined}
          />
          <Calendar
            value={parseISODate(query.endDate)}
            onChange={(e) => onChange({ ...query, endDate: toIso((e.value as Date) ?? null) })}
            dateFormat="dd.mm.yy"
            showIcon
            maxDate={todayDate}
            placeholder={t('home.filters.endDate')}
            aria-label={t('home.filters.endDate')}
            className={rangeError ? 'p-invalid' : undefined}
          />
          {rangeError && (
            <span className="home-filter-error" role="alert">
              {t(rangeError)}
            </span>
          )}
        </div>
      )}
    </FilterBar>
  )
}
